import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Layout } from './components/Layout';
import { Landing } from './pages/Landing';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { Activate } from './pages/Activate';
import { Dashboard } from './pages/Dashboard';
import { Planning } from './pages/Planning';
import { Users } from './pages/Users';
import { Horses } from './pages/Horses';
import { HorseDashboard } from './pages/HorseDashboard';
import { Billing } from './pages/Billing';
import { Settings } from './pages/Settings';
import { Groups } from './pages/Groups';
import { Notifications } from './pages/Notifications';
import { NotificationsAdmin } from './pages/NotificationsAdmin';
import { Guide } from './pages/Guide';
import { SuperAdminDashboard } from './pages/SuperAdminDashboard';
import { SuperAdminRAZ } from './pages/SuperAdminRAZ';
import { SuperAdminActivityLogs } from './pages/SuperAdminActivityLogs';
import { SuperAdminStats } from './pages/SuperAdminStats';
import { ClubInstall } from './pages/ClubInstall';
import { ChangePassword } from './pages/ChangePassword';
import { GalleryPage } from './pages/Gallery';
import Dresstek from './pages/Dresstek';
import './App.css';

// Protected route wrapper
const ProtectedRoute = ({ children, requireInstructor = false, allowPending = false }) => {
  const { user, loading, isInstructor, isSuperAdmin } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // If user has temporary password, force them to change it first
  if (user?.password_temporary && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }
  
  // Super Admin should be redirected to their dashboard, unless they are impersonating
  if (isSuperAdmin() && !user.impersonating) {
    return <Navigate to="/superadmin" replace />;
  }
  
  // Check if user is pending approval
  const isPendingApproval = user?.is_approved === 'en_attente';
  
  // Allowed paths for pending users
  const pendingAllowedPaths = ['/pricing', '/settings'];
  
  // If user is pending and trying to access a non-allowed page, redirect to pricing
  if (isPendingApproval && !allowPending && !pendingAllowedPaths.includes(location.pathname)) {
    return <Navigate to="/pricing" replace />;
  }
  
  if (requireInstructor && !isInstructor()) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Route for password change (requires login but allows temporary password)
const PasswordChangeRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }
  
  // Must be logged in
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // If password is no longer temporary, redirect to dashboard
  if (!user?.password_temporary) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Super Admin route wrapper
const SuperAdminRoute = ({ children }) => {
  const { user, loading, isSuperAdmin } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (!isSuperAdmin()) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Public route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={
        <PublicRoute>
          <Landing />
        </PublicRoute>
      } />
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <Register />
        </PublicRoute>
      } />
      <Route path="/forgot-password" element={
        <PublicRoute>
          <ForgotPassword />
        </PublicRoute>
      } />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/activate" element={<Activate />} />
      <Route path="/change-password" element={
        <PasswordChangeRoute>
          <ChangePassword />
        </PasswordChangeRoute>
      } />
      <Route path="/guide-public" element={<Guide />} />
      <Route path="/install" element={<ClubInstall />} />
      
      {/* Super Admin routes */}
      <Route path="/superadmin" element={
        <SuperAdminRoute>
          <SuperAdminDashboard />
        </SuperAdminRoute>
      } />
      <Route path="/super-admin/raz" element={
        <SuperAdminRoute>
          <SuperAdminRAZ />
        </SuperAdminRoute>
      } />
      <Route path="/super-admin/activity-logs" element={
        <SuperAdminRoute>
          <SuperAdminActivityLogs />
        </SuperAdminRoute>
      } />
      <Route path="/super-admin/stats" element={
        <SuperAdminRoute>
          <SuperAdminStats />
        </SuperAdminRoute>
      } />
      
      {/* Protected routes */}
      <Route element={<Layout />}>
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/planning" element={
          <ProtectedRoute>
            <Planning />
          </ProtectedRoute>
        } />
        <Route path="/users" element={
          <ProtectedRoute requireInstructor>
            <Users />
          </ProtectedRoute>
        } />
        <Route path="/horses" element={
          <ProtectedRoute>
            <Horses />
          </ProtectedRoute>
        } />
        <Route path="/horses/dashboard" element={
          <ProtectedRoute requireInstructor>
            <HorseDashboard />
          </ProtectedRoute>
        } />
        <Route path="/groups" element={
          <ProtectedRoute requireInstructor>
            <Groups />
          </ProtectedRoute>
        } />
        <Route path="/billing" element={
          <ProtectedRoute allowPending>
            <Billing />
          </ProtectedRoute>
        } />
        {/* Redirect old /pricing route to /billing?tab=tarifs */}
        <Route path="/pricing" element={<Navigate to="/billing?tab=tarifs" replace />} />
        <Route path="/settings" element={
          <ProtectedRoute allowPending>
            <Settings />
          </ProtectedRoute>
        } />
        <Route path="/notifications" element={
          <ProtectedRoute>
            <Notifications />
          </ProtectedRoute>
        } />
        <Route path="/notifications/admin" element={
          <ProtectedRoute requireInstructor>
            <NotificationsAdmin />
          </ProtectedRoute>
        } />
        <Route path="/guide" element={
          <ProtectedRoute allowPending>
            <Guide />
          </ProtectedRoute>
        } />
        <Route path="/gallery" element={
          <ProtectedRoute>
            <GalleryPage />
          </ProtectedRoute>
        } />
        <Route path="/dresstek" element={
          <ProtectedRoute>
            <Dresstek />
          </ProtectedRoute>
        } />
      </Route>
      
      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
